import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import type {
  AcceptOrgInvitationRequest,
  GetOrgInvitationResponse,
} from '@nadoutong/contracts';
import { acceptInvitation, getInvitation } from '../api/orgs.ts';
import { PlatformApiError } from '../api/client.ts';
import { AuthPageFrame } from '../components/AuthPageFrame.tsx';
import { formatTimestamp } from '../components/formatters.ts';
import { useToast, coerceErrorMessage } from '../components/Toast.tsx';
import { useSession } from '../hooks/useSession.tsx';
import { useT } from '../i18n/index.ts';
import type { I18nKey, TParams } from '../i18n/index.ts';
import { INPUT_CLASS_ON_LOWEST } from '../styles/forms.ts';

/**
 * Tenant invitation-accept page. Rendered at `/invite/:token`.
 *
 * Two fetches + one submit:
 *   1. On mount, call `GET /v1/invitations/:token` to hydrate the org
 *      name + invited role + expiry, so the user sees what they're
 *      accepting.
 *   2. User supplies password + optional display name (if the invited
 *      email has no existing account) or nothing (if already
 *      authenticated under that email).
 *   3. Submit calls `POST /v1/invitations/:token/accept`. On success,
 *      the session provider re-probes and the caller lands on the
 *      dashboard.
 */

type FetchState =
  | { readonly kind: 'loading' }
  | { readonly kind: 'loaded'; readonly summary: GetOrgInvitationResponse }
  | { readonly kind: 'error'; readonly message: string };

const MIN_PASSWORD_LENGTH = 10;
const MAX_PASSWORD_LENGTH = 128;

function invitationRoleLabel(t: (key: I18nKey, params?: TParams) => string, role: 'admin' | 'member'): string {
  return role === 'admin' ? t('settings.invite_modal.role.admin') : t('settings.invite_modal.role.member');
}

export function Invite(): JSX.Element {
  const { token } = useParams<{ token: string }>();
  const { fetchImpl, refresh, status, user } = useSession();
  const { error: showErrorToast, info: showInfoToast } = useToast();
  const navigate = useNavigate();
  const t = useT();
  const [fetchState, setFetchState] = useState<FetchState>(
    token === undefined || token.length === 0
      ? { kind: 'error', message: t('invite.error.missing_token') }
      : { kind: 'loading' },
  );
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [accepted, setAccepted] = useState(false);

  useEffect(() => {
    if (token === undefined || token.length === 0) {
      return;
    }

    let cancelled = false;
    setFetchState({ kind: 'loading' });

    (async () => {
      try {
        const summary = await getInvitation(token, fetchImpl);
        if (!cancelled) {
          setFetchState({ kind: 'loaded', summary });
        }
      } catch (err) {
        if (cancelled) {
          return;
        }
        const message =
          err instanceof PlatformApiError
            ? `${err.code}: ${err.message}`
            : coerceErrorMessage(err);
        setFetchState({ kind: 'error', message });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, fetchImpl]);

  const activeSessionMatchesInvitee =
    status === 'authenticated' &&
    fetchState.kind === 'loaded' &&
    user?.email.toLowerCase() === fetchState.summary.email.toLowerCase();

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    if (token === undefined || token.length === 0 || fetchState.kind !== 'loaded') {
      return;
    }
    setFormError(null);

    const body: AcceptOrgInvitationRequest = {};
    if (!activeSessionMatchesInvitee) {
      if (password.length < MIN_PASSWORD_LENGTH) {
        setFormError(t('invite.error.password_too_short', { min: MIN_PASSWORD_LENGTH }));
        return;
      }
      if (password.length > MAX_PASSWORD_LENGTH) {
        setFormError(t('invite.error.password_too_long', { max: MAX_PASSWORD_LENGTH }));
        return;
      }
      body.password = password;
      if (displayName.trim().length > 0) {
        body.displayName = displayName.trim();
      }
    }

    setSubmitting(true);
    try {
      await acceptInvitation(token, body, fetchImpl);
      // Re-probe session so Shell / RequireAuth see the fresh identity.
      await refresh();
      setAccepted(true);
      showInfoToast(t('invite.toast.accepted'));
      // Navigate immediately — the success state still renders briefly so
      // E2E assertions can read the confirmation tag.
      navigate('/dashboard', { replace: true });
    } catch (err) {
      const message =
        err instanceof PlatformApiError
          ? `${err.code}: ${err.message}`
          : coerceErrorMessage(err);
      setFormError(message);
      showErrorToast(message);
    } finally {
      setSubmitting(false);
    }
  }

  // If the invitee is already the authenticated user and they've already
  // accepted (we catch 409 INVITATION_ALREADY_ACCEPTED on the server),
  // there's nothing useful to do here — bounce to the dashboard.
  if (accepted) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <AuthPageFrame
      eyebrow={t('invite.eyebrow')}
      title={t('invite.subtitle')}
      description={t('invite.description')}
      maxWidthClassName="max-w-[540px]"
      testId="invite-page-frame"
    >

        {fetchState.kind === 'loading' ? (
          <p className="text-sm text-outline" role="status" data-testid="invite-loading">
            {t('invite.loading')}
          </p>
        ) : null}

        {fetchState.kind === 'error' ? (
          <div
            className="flex flex-col gap-3 rounded-lg bg-error-container p-6 text-on-error-container"
            data-testid="invite-error"
            role="alert"
          >
            <span className="text-[10px] font-bold uppercase tracking-[0.1em]">
              {t('invite.error.title')}
            </span>
            <p className="text-sm">{fetchState.message}</p>
            <Link
              to="/login"
              data-testid="invite-back-to-login"
              className="mt-2 inline-flex w-fit items-center justify-center rounded-lg bg-surface-container-highest px-4 py-2 text-xs font-bold uppercase tracking-[0.08em] text-on-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-error-container"
            >
              {t('common.back_to_login')}
            </Link>
          </div>
        ) : null}

        {fetchState.kind === 'loaded' ? (
          <InviteForm
            summary={fetchState.summary}
            password={password}
            setPassword={setPassword}
            displayName={displayName}
            setDisplayName={setDisplayName}
            submitting={submitting}
            formError={formError}
            activeSessionMatchesInvitee={activeSessionMatchesInvitee}
            activeSessionEmail={user?.email ?? null}
            onSubmit={handleSubmit}
            t={t}
          />
        ) : null}
    </AuthPageFrame>
  );
}

interface InviteFormProps {
  readonly summary: GetOrgInvitationResponse;
  readonly password: string;
  readonly setPassword: (v: string) => void;
  readonly displayName: string;
  readonly setDisplayName: (v: string) => void;
  readonly submitting: boolean;
  readonly formError: string | null;
  readonly activeSessionMatchesInvitee: boolean;
  readonly activeSessionEmail: string | null;
  readonly onSubmit: (e: FormEvent<HTMLFormElement>) => Promise<void>;
  readonly t: (key: I18nKey, params?: TParams) => string;
}

function InviteForm({
  summary,
  password,
  setPassword,
  displayName,
  setDisplayName,
  submitting,
  formError,
  activeSessionMatchesInvitee,
  activeSessionEmail,
  onSubmit,
  t,
}: InviteFormProps): JSX.Element {
  return (
    <form
      className="flex flex-col gap-6"
      onSubmit={(e) => {
        void onSubmit(e);
      }}
      noValidate
      data-testid="invite-form"
    >
      <div
        className="flex flex-col gap-3 rounded-lg bg-surface-container-low p-5"
        data-testid="invite-summary"
      >
        <div className="flex flex-col gap-1">
          <span className="text-[10px] uppercase tracking-[0.1em] text-outline">
            {t('invite.invite_to')}
          </span>
          <span
            className="text-lg font-semibold text-on-surface"
            data-testid="invite-org-name"
          >
            {summary.orgName}
          </span>
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-[10px] uppercase tracking-[0.1em] text-outline">
            {t('invite.role')}
          </span>
          <span className="text-sm font-semibold text-primary" data-testid="invite-role">
            {invitationRoleLabel(t, summary.role)}
          </span>
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-[10px] uppercase tracking-[0.1em] text-outline">
            {t('invite.invitee_email')}
          </span>
          <span className="font-mono text-sm text-on-surface-variant" data-testid="invite-email">
            {summary.email}
          </span>
        </div>
        {summary.invitedByEmail !== null ? (
          <div className="flex flex-col gap-1">
            <span className="text-[10px] uppercase tracking-[0.1em] text-outline">
              {t('invite.invited_by')}
            </span>
            <span
              className="font-mono text-sm text-on-surface-variant"
              data-testid="invite-invited-by"
            >
              {summary.invitedByEmail}
            </span>
          </div>
        ) : null}
        <div className="flex flex-col gap-1">
          <span className="text-[10px] uppercase tracking-[0.1em] text-outline">
            {t('invite.expires')}
          </span>
          <span
            className="text-sm text-on-surface-variant"
            data-testid="invite-expires-at"
          >
            {formatTimestamp(summary.expiresAt)}
          </span>
        </div>
      </div>

      {activeSessionMatchesInvitee ? (
        <p
          className="text-sm text-outline"
          data-testid="invite-session-match"
        >
          {t('invite.session_match', {
            email: activeSessionEmail ?? '',
            org: summary.orgName,
          })}
        </p>
      ) : (
        <>
          {activeSessionEmail !== null ? (
            <p
              className="rounded-lg bg-surface-container-low px-4 py-3 text-xs text-outline"
              data-testid="invite-session-mismatch"
            >
              {t('invite.session_mismatch.body', {
                current: activeSessionEmail,
                target: summary.email,
              })}
              <Link to="/login" className="text-primary underline">
                {t('invite.session_mismatch.switch_link')}
              </Link>
              {t('invite.session_mismatch.suffix')}
            </p>
          ) : null}

          <label className="flex flex-col gap-2">
            <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
              {t('invite.password_with_hint')}
            </span>
            <input
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.currentTarget.value)}
              disabled={submitting}
              required
              minLength={MIN_PASSWORD_LENGTH}
              maxLength={MAX_PASSWORD_LENGTH}
              data-testid="invite-password"
              className={INPUT_CLASS_ON_LOWEST}
            />
          </label>
          <label className="flex flex-col gap-2">
            <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
              {t('invite.display_name')}
            </span>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.currentTarget.value)}
              disabled={submitting}
              maxLength={80}
              placeholder={t('invite.display_name_placeholder')}
              data-testid="invite-display-name"
              className={INPUT_CLASS_ON_LOWEST}
            />
          </label>
        </>
      )}

      {formError !== null ? (
        <p
          className="m-0 text-xs font-medium text-error"
          role="alert"
          data-testid="invite-form-error"
        >
          {formError}
        </p>
      ) : null}

      <button
        type="submit"
        disabled={submitting}
        data-testid="invite-submit"
        className="mt-2 inline-flex items-center justify-center rounded-xl bg-primary-container px-6 py-3 text-sm font-bold uppercase tracking-[0.08em] text-on-primary transition-all hover:shadow-[0_0_16px_rgba(173,198,255,0.2)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface-container active:scale-[0.98] disabled:opacity-40 disabled:pointer-events-none"
      >
        {submitting
          ? t('invite.accepting')
          : activeSessionMatchesInvitee
            ? t('invite.join')
            : t('invite.accept')}
      </button>

      <div className="mt-2 text-xs text-outline">
        <Link
          to="/login"
          data-testid="invite-cancel"
          className="rounded-lg hover:text-on-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface-container"
        >
          {t('invite.cancel')}
        </Link>
      </div>
    </form>
  );
}
